import { useSearchParams } from "react-router-dom";
import { useState, useEffect, useDeferredValue } from "react";

export const useSearchParamsHandler = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const getParam = (key: string) => {
    return searchParams.get(key);
  };

  const setParam = (
    key: string,
    value?: string,
    clearOthers: boolean = false,
  ) => {
    const newParams = clearOthers
      ? new URLSearchParams()
      : new URLSearchParams(searchParams);

    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }

    setSearchParams(newParams, { replace: true });
  };

  const setParams = (
    params: Record<string, string | undefined>,
    clearOthers: boolean = false,
  ) => {
    const newParams = clearOthers
      ? new URLSearchParams()
      : new URLSearchParams(searchParams);

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams, { replace: true });
  };

  const toggleParam = (key: string, activeValue: string = "true") => {
    const currentParams = new URLSearchParams(searchParams);

    if (currentParams.get(key) === activeValue) {
      currentParams.delete(key);
    } else {
      currentParams.set(key, activeValue);
    }

    setSearchParams(currentParams, { replace: true });
  };

  const useDebouncedParam = (key: string, delay = 500) => {
    const query = getParam(key);
    const [inputValue, setInputValue] = useState(query);
    const deferredValue = useDeferredValue(inputValue);

    useEffect(() => {
      const handler = setTimeout(() => {
        setParam(key, deferredValue ?? undefined);
      }, delay);

      return () => clearTimeout(handler);
    }, [deferredValue, delay, key]);

    return { inputValue, setInputValue };
  };

  return { getParam, setParam, setParams, toggleParam, useDebouncedParam };
};
